/**
 * AuthMenu — Header surface for Emergent Google Auth.
 *
 * - When signed-out: shows a "Sign in" pill that redirects to
 *   `https://auth.emergentagent.com/?redirect=<origin>/dashboard`.
 * - When signed-in: shows the user's avatar (or initials fallback) inside
 *   a small chip that opens a dropdown with their name, email, and
 *   "Sign out" action.
 *
 * Loading state is intentionally subtle (faint pill) so it doesn't flash.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */

import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import { LogIn, LogOut, User, Crown, Sparkles, BarChart3, Palette } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useNavigate } from "react-router-dom";
import useAuth from "@/lib/useAuth";
import PricingModal from "@/components/PricingModal";

function _initials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() || "").join("") || "?";
}

export default function AuthMenu() {
  const { user, plan, loading, signIn, signOut } = useAuth();
  const navigate = useNavigate();
  const [pricingOpen, setPricingOpen] = useState(false);
  const isPro = plan?.plan === "pro" && plan?.days_remaining > 0;
  const isOwner = Boolean(plan?.is_owner);

  // iter-178 — let any component (e.g. the new StudioSidebar "Account" item)
  // open the PricingModal via a window event. We listen once at the top-level
  // AuthMenu since it's mounted in the header on every studio page load.
  useEffect(() => {
    const onOpen = () => setPricingOpen(true);
    window.addEventListener("elpm:open-pricing", onOpen);
    return () => window.removeEventListener("elpm:open-pricing", onOpen);
  }, []);

  if (loading) {
    return (
      <span
        data-testid="auth-menu-loading"
        className="inline-flex h-8 items-center gap-2 rounded-full border border-white/[0.07] bg-white/[0.03] px-3 font-mono text-[10px] uppercase tracking-[0.18em] text-white/30"
      >
        <span className="h-2 w-2 animate-pulse rounded-full bg-white/30" />
        Checking…
      </span>
    );
  }

  if (!user) {
    return (
      <>
        <motion.button
          type="button"
          data-testid="auth-signin-btn"
          whileHover={{ y: -1 }}
          whileTap={{ y: 0, scale: 0.98 }}
          transition={{ type: "spring", stiffness: 500, damping: 28 }}
          onClick={() => signIn()}
          aria-label="Sign in"
          className="studio-pill signin-breathe inline-flex whitespace-nowrap items-center gap-2 rounded-full border border-fuchsia-400/35 bg-fuchsia-500/[0.10] px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.18em] text-fuchsia-100 transition hover:border-fuchsia-400/55 hover:bg-fuchsia-500/[0.18] lg:px-3.5"
        >
          <LogIn className="h-4 w-4 lg:h-3.5 lg:w-3.5" />
          <span className="hidden lg:inline">Sign in</span>
        </motion.button>
        {/* Anonymous-pricing entry — visible alongside Sign in for users who
            want to see plans before signing in. Clicking Upgrade in the modal
            still requires sign-in. */}
        <motion.button
          type="button"
          data-testid="auth-pricing-btn"
          whileHover={{ y: -1 }}
          whileTap={{ y: 0, scale: 0.98 }}
          transition={{ type: "spring", stiffness: 500, damping: 28 }}
          onClick={() => setPricingOpen(true)}
          aria-label="Pricing"
          className="studio-pill pricing-sparkle inline-flex items-center gap-2 rounded-full border border-amber-400/35 bg-amber-500/[0.10] px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.18em] text-amber-100 transition hover:border-amber-400/55 hover:bg-amber-500/[0.18] lg:px-3.5"
        >
          <Crown className="h-4 w-4 lg:h-3.5 lg:w-3.5" />
          <span className="hidden lg:inline">Pricing</span>
        </motion.button>
        <PricingModal open={pricingOpen} onClose={() => setPricingOpen(false)} />
      </>
    );
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            data-testid="auth-user-chip"
            className={`studio-pill inline-flex items-center gap-2 rounded-full px-2 py-1 pr-3 font-mono text-[10.5px] uppercase tracking-[0.18em] transition ${
              isOwner
                ? "border border-fuchsia-400/45 bg-gradient-to-r from-fuchsia-500/[0.18] to-amber-500/[0.18] text-fuchsia-50 hover:border-fuchsia-300/70 hover:from-fuchsia-500/[0.28] hover:to-amber-500/[0.28]"
                : isPro
                ? "border border-amber-400/40 bg-amber-500/[0.12] text-amber-100 hover:border-amber-400/65 hover:bg-amber-500/[0.22]"
                : "border border-emerald-400/30 bg-emerald-500/[0.08] text-emerald-100 hover:border-emerald-400/55 hover:bg-emerald-500/[0.18]"
            }`}
          >
            {user.picture ? (
              <img
                src={user.picture}
                alt=""
                referrerPolicy="no-referrer"
                className="h-6 w-6 rounded-full ring-1 ring-white/15"
              />
            ) : (
              <span className={`grid h-6 w-6 place-items-center rounded-full bg-gradient-to-br text-[10px] font-bold text-white ${
                isOwner ? "from-fuchsia-400 via-rose-500 to-amber-400"
                  : isPro ? "from-amber-400 to-rose-500"
                  : "from-emerald-400 to-sky-500"
              }`}>
                {_initials(user.name)}
              </span>
            )}
            <span data-testid="auth-user-name" className="hidden max-w-[120px] truncate lg:inline">
              {user.name?.split(/\s+/)[0] || "Account"}
            </span>
            {isOwner ? (
              <span data-testid="auth-owner-badge" className="rounded-full bg-gradient-to-r from-fuchsia-500/40 to-amber-500/40 px-1.5 py-0.5 font-mono text-[8.5px] uppercase tracking-wider text-white ring-1 ring-fuchsia-300/60">
                Owner
              </span>
            ) : isPro && (
              <span data-testid="auth-pro-badge" className="rounded-full bg-amber-500/30 px-1.5 py-0.5 font-mono text-[8.5px] uppercase tracking-wider text-amber-50 ring-1 ring-amber-300/50">
                Pro
              </span>
            )}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          data-testid="auth-user-menu"
          className="glass-card w-72 rounded-2xl border-0 text-white"
        >
          <DropdownMenuLabel className="px-3 pb-2 pt-3">
            <div className="flex items-center gap-3">
              {user.picture ? (
                <img
                  src={user.picture}
                  alt=""
                  referrerPolicy="no-referrer"
                  className="h-9 w-9 rounded-full ring-1 ring-white/15"
                />
              ) : (
                <span className="grid h-9 w-9 place-items-center rounded-full bg-gradient-to-br from-fuchsia-400 to-sky-500 text-xs font-bold text-white">
                  {_initials(user.name)}
                </span>
              )}
              <div className="min-w-0">
                <p className="truncate font-display text-[13px] font-semibold leading-tight text-white">
                  {user.name || "Signed in"}
                </p>
                <p data-testid="auth-user-email" className="truncate font-mono text-[10px] uppercase tracking-wider text-white/55">
                  {user.email}
                </p>
              </div>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator className="bg-white/10" />
          {/* Plan row — clickable, opens pricing modal. */}
          <DropdownMenuItem
            data-testid="auth-plan-row"
            onClick={() => setPricingOpen(true)}
            className="cursor-pointer rounded-md px-3 py-2 text-[12px] focus:bg-white/[0.06] focus:text-white"
          >
            {isOwner ? (
              <>
                <Crown className="mr-2 h-3.5 w-3.5 text-fuchsia-300" />
                <span className="bg-gradient-to-r from-fuchsia-200 to-amber-200 bg-clip-text font-semibold text-transparent">
                  OWNER · Unlimited access
                </span>
              </>
            ) : isPro ? (
              <>
                <Crown className="mr-2 h-3.5 w-3.5 text-amber-300" />
                <span className="text-amber-100">PRO member · {plan.days_remaining} day{plan.days_remaining === 1 ? "" : "s"} left</span>
              </>
            ) : (
              <>
                <Sparkles className="mr-2 h-3.5 w-3.5 text-fuchsia-300" />
                <span className="text-white">Pro membership</span>
                <span className="ml-auto font-mono text-[10px] uppercase tracking-wider text-fuchsia-200/90">from $9.99 USD</span>
              </>
            )}
          </DropdownMenuItem>
          {isOwner && (
            <DropdownMenuItem
              data-testid="auth-owner-dashboard-link"
              onClick={() => navigate("/owner")}
              className="cursor-pointer rounded-md px-3 py-2 text-[12px] focus:bg-fuchsia-500/15 focus:text-fuchsia-100"
            >
              <BarChart3 className="mr-2 h-3.5 w-3.5 text-fuchsia-300" />
              <span className="bg-gradient-to-r from-fuchsia-200 to-amber-200 bg-clip-text font-semibold text-transparent">
                Owner console
              </span>
              <span className="ml-auto font-mono text-[9.5px] uppercase tracking-wider text-white/35">
                analytics
              </span>
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            data-testid="auth-settings-link"
            onClick={() => navigate("/settings")}
            className="cursor-pointer rounded-md px-3 py-2 text-[12px] focus:bg-white/[0.06] focus:text-white"
          >
            <Palette className="mr-2 h-3.5 w-3.5 text-fuchsia-300" />
            <span>Settings</span>
            <span className="ml-auto font-mono text-[9.5px] uppercase tracking-wider text-white/35">
              theme · prefs
            </span>
          </DropdownMenuItem>
          <DropdownMenuItem
            className="cursor-default rounded-md px-3 py-2 text-[12px] focus:bg-white/[0.06] focus:text-white"
            disabled
            data-testid="auth-account-id"
          >
            <User className="mr-2 h-3.5 w-3.5 text-white/55" />
            <span className="font-mono text-[10px] uppercase tracking-wider text-white/50">
              ID · {user.user_id?.slice(0, 14)}…
            </span>
          </DropdownMenuItem>
          <DropdownMenuItem
            data-testid="auth-signout-btn"
            onClick={signOut}
            className="cursor-pointer rounded-md px-3 py-2 text-[12px] focus:bg-rose-500/15 focus:text-rose-100"
          >
            <LogOut className="mr-2 h-3.5 w-3.5 text-rose-300" />
            <span className="text-rose-200">Sign out</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <PricingModal open={pricingOpen} onClose={() => setPricingOpen(false)} />
    </>
  );
}
